'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { RiMenuFill } from 'react-icons/ri';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const navigationLabel = locale === 'en' ? 'Navigation' : 'Navigácia';
  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={navigationLabel}
          className={cn(
            'text-primary',
            'ea-mobile-sidebar__trigger flex items-center justify-end gap-4 lg:gap-10',
            props.className?.trigger,
          )}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-none border-[5px] border-primary">
            <RiMenuFill size={20} />
          </div>
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          className={cn(
            'w-screen justify-between overflow-scroll border-none bg-transparent p-0 sm:w-[400px] md:bg-black/50',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{navigationLabel}</SheetTitle>
          <div className=" px-3">
            {!!props.menu && (
              <div className="flex flex-col gap-4 p-4">
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full items-center justify-center"
                        prefetch={false}
                      >
                        <div className={cn('text-lg font-bold text-white', 'text-center')}>
                          {item.name}
                        </div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
